import { useTranslation } from 'react-i18next'
import { QualifiedProspectsFilters } from '../../types'
import { getPipelineAnalysis } from '../../services'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Building2, Server, TrendingUp, Filter } from 'lucide-react'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
} from 'recharts'
import dayjs from 'dayjs'
import { SCHEMES } from '@/components/EchartCustomized'

interface PipelineAnalysisProps {
    filters: QualifiedProspectsFilters
}

const JOURNEY_COLORS = {
    searching: SCHEMES.info[0],
    contact: SCHEMES.brand[5],
    presentation: SCHEMES.warning[0],
    poc: SCHEMES.success[2],
    deal: SCHEMES.brand[6],
    contract: SCHEMES.success[0],
    no_journey: SCHEMES.mono.gray[2],
}

const SERVICE_COLORS = SCHEMES.default.slice(0, 8)

const PipelineAnalysis: React.FC<PipelineAnalysisProps> = ({ filters }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'pipeline-analysis',
                filters.from_date,
                filters.to_date,
                filters.granularity,
            ],
            queryFn: () => getPipelineAnalysis(filters),
            enabled: !!filters.from_date && !!filters.to_date,
        },
        {
            loadingComponent: (
                <div className="flex justify-center items-center h-[400px]">
                    <Loading.CircularProgress width={32} height={32} />
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data) {
        return (
            <div className="flex justify-center items-center h-[300px] text-gray-500">
                {t('no_data_available')}
            </div>
        )
    }

    const {
        industry_breakdown,
        service_interest_breakdown,
        funnel_conversion,
        new_customers_trend,
    } = data

    // Format trend data
    const trendData = new_customers_trend
        .map((item) => ({
            period: dayjs(item.period).format('DD/MM'),
            fullPeriod: item.period,
            customers: item.new_customers,
        }))
        .reverse()

    return (
        <div className="space-y-4">
            {/* New Customers Trend - Area Chart */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-green-600" size={20} />
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('qualified_prospects.new_customers_trend')}
                    </Typogaphy>
                </div>
                {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                        <AreaChart
                            data={trendData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient
                                    id="colorCustomers"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={SCHEMES.success[0]}
                                        stopOpacity={0.8}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={SCHEMES.success[0]}
                                        stopOpacity={0.1}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="period"
                                stroke="#9ca3af"
                                fontSize={11}
                            />
                            <YAxis stroke="#9ca3af" fontSize={11} />
                            <Tooltip
                                labelFormatter={(_, payload) => {
                                    const item = payload?.[0]?.payload
                                    return item
                                        ? dayjs(item.fullPeriod).format(
                                              'DD/MM/YYYY'
                                          )
                                        : ''
                                }}
                                formatter={(value: number) => [
                                    value.toLocaleString(),
                                    t('qualified_prospects.new_customers'),
                                ]}
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="customers"
                                stroke={SCHEMES.success[0]}
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorCustomers)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex justify-center items-center h-[250px] text-gray-500">
                        {t('no_data_available')}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Service Interest Breakdown - Pie Chart */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <Server className="text-purple-600" size={20} />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('qualified_prospects.service_interest')}
                        </Typogaphy>
                    </div>
                    {service_interest_breakdown.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={service_interest_breakdown as any}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="customer_count"
                                    nameKey="service_interest"
                                    label={(props: any) =>
                                        `${props.service_interest} (${(props.percent * 100).toFixed(0)}%)`
                                    }
                                    labelLine={false}
                                >
                                    {service_interest_breakdown.map(
                                        (_, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    SERVICE_COLORS[
                                                        index %
                                                            SERVICE_COLORS.length
                                                    ]
                                                }
                                            />
                                        )
                                    )}
                                </Pie>
                                <Tooltip
                                    formatter={(value: number) => [
                                        value.toLocaleString(),
                                        t('customers'),
                                    ]}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex justify-center items-center h-[300px] text-gray-500">
                            {t('no_data_available')}
                        </div>
                    )}
                </div>

                {/* Funnel Conversion */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="text-blue-600" size={20} />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('qualified_prospects.funnel_conversion')}
                        </Typogaphy>
                    </div>
                    {funnel_conversion.length > 0 ? (
                        <div className="overflow-auto max-h-[300px]">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {t(
                                                'qualified_prospects.journey_step'
                                            )}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.status')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.count')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            %
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {funnel_conversion.map((item, index) => (
                                        <tr
                                            key={index}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {t(
                                                    `qualified_prospects.journey_steps.${item.journey_step}`
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                                                {t(
                                                    `qualified_prospects.statuses.${item.step_status}`
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-blue-600">
                                                {item.customer_count.toLocaleString()}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                                                {item.percentage.toFixed(1)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-[300px] text-gray-500">
                            {t('no_data_available')}
                        </div>
                    )}
                </div>
            </div>

            {/* Industry Breakdown - Stacked Bar Chart */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <Building2 className="text-amber-600" size={20} />
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('qualified_prospects.industry_breakdown')}
                    </Typogaphy>
                </div>
                {industry_breakdown.length > 0 ? (
                    <>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart
                                data={industry_breakdown}
                                layout="vertical"
                                margin={{ left: 80, right: 30 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#e5e7eb"
                                />
                                <XAxis
                                    type="number"
                                    stroke="#9ca3af"
                                    fontSize={11}
                                />
                                <YAxis
                                    dataKey="industry"
                                    type="category"
                                    stroke="#9ca3af"
                                    fontSize={11}
                                    width={80}
                                    tickLine={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                    }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="searching"
                                    name={t(
                                        'qualified_prospects.journey_steps.searching'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.searching}
                                />
                                <Bar
                                    dataKey="contact"
                                    name={t(
                                        'qualified_prospects.journey_steps.contact'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.contact}
                                />
                                <Bar
                                    dataKey="presentation"
                                    name={t(
                                        'qualified_prospects.journey_steps.presentation'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.presentation}
                                />
                                <Bar
                                    dataKey="poc"
                                    name={t(
                                        'qualified_prospects.journey_steps.poc'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.poc}
                                />
                                <Bar
                                    dataKey="deal"
                                    name={t(
                                        'qualified_prospects.journey_steps.deal'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.deal}
                                />
                                <Bar
                                    dataKey="contract"
                                    name={t(
                                        'qualified_prospects.journey_steps.contract'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.contract}
                                />
                                <Bar
                                    dataKey="no_journey"
                                    name={t(
                                        'qualified_prospects.journey_steps.no-journey'
                                    )}
                                    stackId="a"
                                    fill={JOURNEY_COLORS.no_journey}
                                />
                            </BarChart>
                        </ResponsiveContainer>

                        {/* Industry Summary Table */}
                        <div className="mt-4 overflow-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.industry')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('total')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            Searching
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            Contact
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            Presentation
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            POC
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            Deal
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            Contract
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            No Journey
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {industry_breakdown.map((item) => (
                                        <tr
                                            key={item.industry}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">
                                                {item.industry}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right font-bold text-gray-900">
                                                {item.total_customers}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-blue-600">
                                                {item.searching}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-purple-600">
                                                {item.contact}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-amber-600">
                                                {item.presentation}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-emerald-600">
                                                {item.poc}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-cyan-600">
                                                {item.deal}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-green-600">
                                                {item.contract}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-right text-gray-500">
                                                {item.no_journey}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : (
                    <div className="flex justify-center items-center h-[300px] text-gray-500">
                        {t('no_data_available')}
                    </div>
                )}
            </div>
        </div>
    )
}

export default PipelineAnalysis
